import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Product lightbox: one contained media item at a time.
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 * @property {HTMLElement} counter
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #index = 0;
  #controller;
  #opener;
  #touchStart;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this.refs;
    dialog.addEventListener('close', this.#onClose, { signal });
    dialog.addEventListener('cancel', this.#onCancel, { signal });
    dialog.addEventListener('click', this.#onBackdropClick, { signal });
    dialog.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    dialog.addEventListener('touchend', this.#onTouchEnd, { signal, passive: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.refs.dialog.open) this.closeDialog();
    super.disconnectedCallback();
  }

  /** @param {number} index @param {PointerEvent} event */
  open(index, event) {
    event?.preventDefault();
    if (event?.target instanceof Element) {
      this.#opener = event.target.closest('button, a, [tabindex]') || event.target.closest('slideshow-slide, li');
    }
    this.selectThumbnail(Number(index));
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.refs.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus();
  }

  /** Keep the public media-gallery preload contract; use responsive image sources. */
  loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;
    image.loading = 'eager';
  }

  close() {
    this.closeDialog();
  }

  closeDialog() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  #onClose = () => {
    this.#touchStart = undefined;
    this.#pauseMedia();
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) {
      if (!this.#opener.matches('button, a, [tabindex]')) this.#opener.tabIndex = -1;
      this.#opener.focus({ preventScroll: true });
    }
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog || event.target instanceof Element && event.target.matches('.dialog-zoomed-gallery')) this.close();
  };

  /** @param {KeyboardEvent} event */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.selectThumbnail(this.#index + (event.key === 'ArrowRight' ? direction : -direction));
    }
  }

  previous() { this.selectThumbnail(this.#index - 1); }
  next() { this.selectThumbnail(this.#index + 1); }

  /** @param {number} index */
  handleThumbnailClick(index) { this.selectThumbnail(Number(index)); }

  /** @param {number} index */
  handleThumbnailPointerEnter(index) { this.loadHighResolutionImage(this.refs.media[Number(index)]); }

  /** @param {number} index */
  selectThumbnail(index) {
    const { media, thumbnails, counter } = this.refs;
    if (!media.length || !Number.isFinite(index)) return;
    const nextIndex = ((index % media.length) + media.length) % media.length;
    if (nextIndex !== this.#index) this.#pauseMedia();
    this.#index = nextIndex;
    media.forEach((item, i) => {
      item.hidden = i !== nextIndex;
      item.setAttribute('aria-hidden', String(i !== nextIndex));
    });
    const buttons = [...thumbnails.querySelectorAll('button')];
    buttons.forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === nextIndex));
      if (i === nextIndex) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    const thumbnail = buttons[nextIndex];
    if (thumbnail) {
      thumbnails.scrollTo({
        left: Math.max(0, thumbnail.offsetLeft - thumbnails.clientWidth / 2 + thumbnail.clientWidth / 2),
        top: Math.max(0, thumbnail.offsetTop - thumbnails.clientHeight / 2 + thumbnail.clientHeight / 2),
        behavior: 'instant',
      });
    }
    if (counter) {
      counter.textContent = (nextIndex + 1) + ' / ' + media.length;
      counter.setAttribute('aria-label', thumbnail?.getAttribute('aria-label') || counter.textContent);
    }
    this.loadHighResolutionImage(media[nextIndex]);
    if (this.refs.dialog.open) this.dispatchEvent(new ZoomMediaSelectedEvent(nextIndex));
  }

  #pauseMedia() {
    this.refs.dialog.querySelectorAll('video').forEach((video) => video.pause());
    this.refs.dialog.querySelectorAll('iframe').forEach((frame) => {
      if (/youtube(?:-nocookie)?\.com/.test(frame.src)) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
      else if (/player\.vimeo\.com/.test(frame.src)) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    });
  }

  #onTouchStart = (event) => {
    this.#touchStart = undefined;
    if (event.touches.length !== 1 || !(event.target instanceof Element) || !event.target.closest('.product-media-container--image')) return;
    this.#touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  };

  #onTouchEnd = (event) => {
    const start = this.#touchStart;
    this.#touchStart = undefined;
    if (!start || event.touches.length || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.selectThumbnail(this.#index + (dx < 0 ? direction : -direction));
    }
  };
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
